import React, { ReactElement, useCallback } from 'react';
import classNames from 'classnames';
import { PRODUCT_BOX_STYLE_TYPE, ProductList } from 'b2b-components/theme/components/ProductList';
import { isMobile } from 'b2b-components/utils/device';
import { isPhone } from 'b2b-apps/core/common/utils/env';
import { ADD_TO_CART_DISPLAY_TYPE, AddToCart } from 'b2b-apps/shop/cart/components/AddToCart/AddToCart';
import { CopyableText } from 'b2b-apps/shop/common/components/CopyableText';
import { HiddenPrice } from 'b2b-apps/shop/common/components/HiddenPrice/HiddenPrice';
import { FailedProductBox } from 'b2b-apps/shop/common/components/ProductBox/ProductBox/FailedProductBox';
import { useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { AddToCompareAction } from 'b2b-apps/shop/product/components/ProductActions/AddToCompareAction';
import {
  ADD_TO_WISHLIST_STYLE_TYPE,
  AddToWishlistAction,
} from 'b2b-apps/shop/product/components/ProductActions/AddToWishlistAction';
import { ROUTES } from 'b2b-apps/shop/routes/components/Router';
import { ComponentType, Props } from './';
import './ProductBox.scss';

const ProductBox: ComponentType = ({
  product: productData,
  sku,
  deviceType,
  category,
  imageUrl,
  variants,
  isAddToCartHidden,
  areVariantsHidden,
  isForSlider,
  isFromContentList,
  mptId,
  push,
  urlCreator,
  styleType,
  className,
  onClick,
  onAddToCart,
}: Props): ReactElement | null => {
  const t = useTranslate();
  const {
    data: product,
    hasFailed,
    isLoaded,
    isLoading,
  } = productData || {};

  const productBoxAdditionalProps = {
    dataAttributes: {},
  };

  if (mptId) {
    productBoxAdditionalProps.dataAttributes = {
      'data-mpt-id': mptId,
    };
  }

  const rootClassName = classNames(
    'cds-ProductBox',
    {
      'cds-ProductBox--List': styleType === PRODUCT_BOX_STYLE_TYPE.list,
    },
    className,
  );

  const renderActions = useCallback(
    (visible: boolean): ReactElement | null => {
      if (!isLoaded || !product) {
        return null;
      }

      const {
        description: { title },
        effectivePricing: { isCertified },
        settings,
        sku: productSku,
      } = product;

      return (
        <>
          <li className="cds-ProductBox-Action">
            {!isPhone(deviceType) && (
              <AddToCompareAction
                sku={product.sku}
                isVisible={visible}
              />
            )}
          </li>
          <li className="cds-ProductBox-Action">
            <AddToWishlistAction
              skus={[productSku]}
              styleType={ADD_TO_WISHLIST_STYLE_TYPE.icon}
              className={classNames({
                'cds-ProductBox-Action--Hidden': !isMobile(deviceType) && !visible,
              })}
            />
          </li>

          {!isAddToCartHidden && (
            <li className="cds-ProductBox-Action">
              <AddToCart
                isUserCertified={isCertified}
                productName={title}
                settings={settings}
                sku={productSku}
                type={ADD_TO_CART_DISPLAY_TYPE.icon}
                isVisible={visible}
                onClick={onAddToCart}
              />
            </li>
          )}
        </>
      );
    },
    [isLoaded, product, isAddToCartHidden, deviceType],
  );

  const handleRenderSku = useCallback(
    (skuNumber: string): ReactElement => (
      <CopyableText>{skuNumber}</CopyableText>
    ),
    [],
  );

  const renderHiddenPrice = useCallback(
    (isHiddenPriceAndNotCertified: boolean): ReactElement => (
      <HiddenPrice
        className="ProductBox-HiddenPrice"
        hasCurrencyCode={styleType !== PRODUCT_BOX_STYLE_TYPE.table}
        isHiddenPriceAndNotCertified={isHiddenPriceAndNotCertified}
      />
    ),
    [styleType],
  );

  if (isLoading) {
    return <ProductList.ProductBoxSkeleton styleType={styleType} />;
  }

  if (hasFailed || (isLoaded && !product)) {
    if (isFromContentList) {
      return null;
    }

    if (styleType === PRODUCT_BOX_STYLE_TYPE.table) {
      return (
        <tr className="cds-ProductBoxTable-BodyRow">
          <td colSpan={7}>
            <FailedProductBox sku={sku} styleType={styleType} />
          </td>
        </tr>
      );
    }

    return (
      <li className="cds-ProductBoxWrapper">
        <FailedProductBox
          sku={sku}
          styleType={styleType}
          className={rootClassName}
        />
      </li>
    );
  }

  if (!isLoaded) {
    return null;
  }

  const text = {
    assortment: t('Assortment'),
    category: t('Category'),
    manufacturerSku: t('Manufacturer Product ID'),
    coreAssortment: t('Core assortment'),
    ownAssortment: t('Customer specific assortment'),
    new: t('new flag', {}, 'Product flag icon - "new" label'),
    occasion: t('verified return flag', {}, 'Product flag icon - "verified return" label'),
    promotion: t('special offer flag', {}, 'Product flag icon - "special offer" label'),
    sellout: t('sale flag', {}, 'Product flag icon - "sale" label'),
    sku: t('SKU'),
    stockCount: {
      date: t('Date'),
      deliveryDates: t('Expected delivery dates'),
      notReadyForShipment: t('Not ready for shipment'),
      orderedQuantity: t('Ordered Quantity'),
      readyForShipment: t(
        [
          '<strong>1</strong> pc. ready for shipment',
          '<strong>{count}</strong> pcs. ready for shipment',
          'count',
        ],
        {
          count: product?.settings.quantity || 0,
        },
      ),
      unknownDeliveryDate: t('Delivery date unknown'),
      specialOrder: {
        full: t('Special Order'),
        short: t('S.O.'),
        explanation: t('We do not stock this item, it will be ordered for you after your order. You will receive an email with details about delivery time.'),
      },
      seeMore: t('see more'),
    },
    variants: t('Also available in:'),
  };

  if (!product) {
    return null;
  }

  const {
    description,
    effectivePricing,
    manufacturer,
    settings: {
      isNew,
      isOccasion,
      isSellout,
      isSpecialOffer,
      isSpecialOrder,
      quantity,
      quantityOrdered,
      isCoreAssortment,
      isOwnAssortment,
    },
    sku: productSku,
  } = product;

  return (
    <ProductList.ProductBox
      {...productBoxAdditionalProps}
      basePrice={effectivePricing.basePrice}
      bulletPoints={description.bulletPoints}
      category={category}
      className={className}
      deviceType={deviceType}
      areVariantsHidden={areVariantsHidden}
      isForSlider={isForSlider}
      energyLabel={
        description.energyLabel
          ? {
            range: description.energyLabel.range,
            rating: description.energyLabel.value,
          }
          : null
      }
      imageUrl={imageUrl}
      isNew={isNew}
      isOccasion={isOccasion}
      isPromotion={isSpecialOffer}
      isSellout={isSellout}
      isSpecialOrder={isSpecialOrder}
      isCoreAssortment={isCoreAssortment}
      isOwnAssortment={isOwnAssortment}
      manufacturerSku={manufacturer.productId}
      onCategoryClick={push}
      onProductClick={onClick}
      price={effectivePricing.mainPrice}
      quantityOrdered={quantityOrdered}
      renderActions={renderActions}
      renderHiddenPrice={
        effectivePricing.isHiddenPrice
          ? renderHiddenPrice(effectivePricing.isHiddenPriceAndNotCertified)
          : null
      }
      sku={productSku}
      renderSku={handleRenderSku}
      stock={quantity || 0}
      styleType={styleType}
      text={text}
      title={description.title}
      url={urlCreator(ROUTES.product, {
        sku: productSku,
      })}
      variantCount={3}
      variants={variants}
    />
  );
};

export { ProductBox };
